import { SUPPORTED_OVERFLOW_LOCALES, getServiceMpvId } from '@99designs/design-services-common';
import { useBriefContext } from '../../../BriefContext';
import { useSelectProductWithCacheUpdate } from '../ClientConfiguredFields/ProductDropdown/useSelectProductMutation';
import { useUpdateField } from '../useUpdateField';
import { useBriefSetGridChoiceMutation } from './briefSetGridChoice.generated';

const DESIGN_LIVE_SERVICE_MPV_ID = 'designLive';
const CARE_DESIGN_SERVICE_MPV_ID = 'careDesign';

export function useUpdateGridChoiceField(
    key: string,
    locale: string,
    briefId?: string,
    initialValue?: string | null
) {
    const { selectProduct } = useSelectProductWithCacheUpdate();
    const { serviceProductMpvId, printProductMpvId } = useBriefContext();

    const [setGridChoice] = useBriefSetGridChoiceMutation({
        onCompleted: (briefData) => {
            if (key !== 'requestType') {
                return;
            }
            const field = briefData.setField.dynamicFields.find((field) => field.id === key);
            if (!field) {
                return;
            }
            if (field.__typename !== 'GridChoiceField') {
                throw new Error(
                    "Field is not a GridChoiceField. This schema is not compatible with the fields currently in use on the front end. And will cause all kinds of trouble. If you're seeing this error in bugsnag, investigate immediately"
                );
            }
            if (briefData.setField.correlationId && serviceProductMpvId) {
                selectProduct({
                    variables: {
                        requestId: briefData.setField.correlationId,
                        input: {
                            serviceProductMpvId: newServiceProductMpvId(
                                field.choiceValue || '',
                                locale || '',
                                serviceProductMpvId,
                                printProductMpvId
                            ),
                            printProductMpvId: [], // this does not unset the physical product.
                        },
                    },
                });
            }
        },
    });
    return useUpdateField<string>({ key, setField: setGridChoice, briefId, initialValue });
}

export function newServiceProductMpvId(
    fieldChoiceValue: string,
    locale: string,
    existingServiceProductMpvId: string,
    printProductMpvId?: string
) {
    if (fieldChoiceValue === 'DesignLive') {
        /**
         * Fallback to legacy DesignLive mpvId
         * because the new service products don't support DesignLive yet.
         */
        return DESIGN_LIVE_SERVICE_MPV_ID;
    }

    if (existingServiceProductMpvId.toLowerCase() !== DESIGN_LIVE_SERVICE_MPV_ID.toLowerCase()) {
        /**
         * Avoid unnecessary esmpvId changes existing service product hasn't been overridden to DesignLive
         */
        return existingServiceProductMpvId;
    }

    /**
     * From here onward we have lost the information about the originally selected service product
     * so we need to determine which service product to switch to based on the criteria below
     */

    const isOverflowLocale = SUPPORTED_OVERFLOW_LOCALES.find(
        (supportedLocale) => supportedLocale.toLowerCase() === locale.toLowerCase()
    );
    if (!isOverflowLocale) {
        return CARE_DESIGN_SERVICE_MPV_ID;
    }

    const newServiceProductMpvId = getServiceMpvId(printProductMpvId || '');
    if (!newServiceProductMpvId) {
        return CARE_DESIGN_SERVICE_MPV_ID;
    }

    return newServiceProductMpvId;
}
